/*
 * Copyright (c) CIRROM 2026.
 */

import { useCallback, useEffect, useRef, useState } from "react";

/*
 * A window to change your mind, in place of a rollback we cannot offer.
 *
 * Writes from this surface reach the customer's CRM and there is no undo once
 * they land. Rather than pretend otherwise, the decision is held here for a
 * few seconds before anything leaves the browser: undo simply cancels a
 * timer, so the "rollback" is real precisely because the request never
 * happened. Nothing is optimistic and nothing has to be reversed.
 *
 * One decision is held at a time. Queueing a second commits the first
 * immediately, because a steward who has moved to the next field has made up
 * their mind about the last one - and holding a growing pile of pending
 * writes would make it impossible to say what state the record is in.
 */
export const UNDO_WINDOW_MS = 5000;

export interface QueuedWrite {
  /**
   * The field this decision belongs to (or "bulk"), so the row that owns it
   * can refuse a second click while it waits.
   */
  key: string;
  /** Snackbar copy, past tense: "Job Title approved". */
  label: string;
  /** Fires when the window closes. Not called at all if undone. */
  run: () => void;
}

export const useUndoableWrite = (scopeKey?: string | number) => {
  const [queued, setQueued] = useState<QueuedWrite | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const queuedRef = useRef<QueuedWrite | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  };

  const commit = useCallback(() => {
    clearTimer();
    const item = queuedRef.current;
    queuedRef.current = null;
    setQueued(null);
    item?.run();
  }, []);

  const queue = useCallback(
    (item: QueuedWrite) => {
      commit();
      queuedRef.current = item;
      setQueued(item);
      timerRef.current = setTimeout(commit, UNDO_WINDOW_MS);
    },
    [commit]
  );

  const undo = useCallback(() => {
    clearTimer();
    queuedRef.current = null;
    setQueued(null);
  }, []);

  /*
   * Leaving commits. The window exists for regret, not as a way to discard
   * work the steward explicitly asked for - a decision that silently
   * evaporated because they navigated away would be far worse than one that
   * landed a moment early.
   */
  useEffect(() => {
    if (scopeKey === undefined) return;
    return () => {
      clearTimer();
      const item = queuedRef.current;
      queuedRef.current = null;
      /*
       * The snackbar leaves with the write. Clearing only the ref would strand
       * an Undo button over a decision that has already gone to the server -
       * pressing it would dismiss the bar and report a cancellation that never
       * happened. On unmount this is a no-op; on a scope change it is the
       * whole point.
       */
      setQueued(null);
      item?.run();
    };
  }, [scopeKey]);

  return { queued, queue, undo, commit };
};
